

export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout> | undefined;

	return function(...args: Parameters<T>) {
		if (timeout) {
			clearTimeout(timeout);
		}

		timeout = setTimeout(() => {
			func(...args);
		}, wait);
	};
}

/**
 * ----*--*-----*--------------------------------*******----*------------
 * ----!------------------!----------------------!--------------------!--
 *     A                  B                      C                    D
 *
 * A - called because it is the first attempted call
 * B - called because there was 10ms calm period and pending calls
 * C - first call after 10 ms of calm
 * D - called because there was 10ms calm period and pending calls
 *
 * @param fn
 * @param calmPeriod
 */
export function calmTrigger<T extends (...args: any[]) => void>(fn: T, calmPeriod: number): T {
	let lastCallTime = 0;
	let lastAttemptTime = 0;
	let timeout: any = null;
	let pendingArgs: any[] | null = null;

	return function(this: any, ...args: any[]) {
		const now = Date.now();
		const timeSinceLastCall = now - lastCallTime;
		const timeSinceLastAttempt = now - lastAttemptTime;

		lastAttemptTime = now;

		if (timeSinceLastCall === 0 || timeSinceLastCall >= calmPeriod) {
			// Case A or C: first call or after calm period
			lastCallTime = now;
			fn.apply(this, args);
		} else {
			// Case B or D: schedule pending call if not already scheduled
			pendingArgs = args;
			if (timeout) clearTimeout(timeout);
			timeout = setTimeout(() => {
				if (Date.now() - lastAttemptTime >= calmPeriod) {
					lastCallTime = Date.now();
					fn.apply(this, pendingArgs!);
					pendingArgs = null;
					timeout = null;
				}
			}, calmPeriod);
		}
	} as T;
}
